import { MessageItem } from "@repo/ui/chat/messageItem";
import { MessageTimeline } from "@repo/ui/chat/messageTimeline";
import { UserMessageForm } from "@repo/ui/chat/userMessageForm";
import { type FC, useEffect, useEffectEvent, useState } from "react";

interface InterviewSimulationProps {
  getNextMessage: (
    messages: readonly ChatMessage[],
  ) => Promise<{ messages: ChatMessage[] }>;
  resetKey?: unknown;
}

// Chat message type for interview simulation
export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export const InterviewSimulation: FC<InterviewSimulationProps> = ({
  getNextMessage,
  resetKey,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function initializeConversation() {
    resetConversation();
    setIsLoading(true);
    setError(null);
    try {
      const response = await getNextMessage([]);
      setMessages(response.messages);
    } catch (err) {
      console.error("Error initializing conversation:", err);
      setError("Failed to start conversation. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  const onReset = useEffectEvent(() => {
    initializeConversation();
  });

  // Reset and initialize conversation when resetKey changes
  useEffect(() => {
    onReset();
  }, [resetKey]);

  const resetConversation = () => {
    setMessages([]);
    setIsLoading(false);
    setError(null);
  };

  const handleMessageSend = async (message: string) => {
    if (isLoading) return;

    // Add user message to chat
    const userMessage: ChatMessage = {
      role: "user",
      content: message,
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const response = await getNextMessage([...messages, userMessage]);

      setMessages(response.messages);
    } catch (err: any) {
      console.error("Error in interview simulation:", err);
      // Display specific error message from API if available
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Messages Section */}
      <MessageTimeline messages={messages} isLoading={isLoading}>
        {(message) => (
          <MessageItem role={message.role}>{message.content}</MessageItem>
        )}
      </MessageTimeline>

      {/* Input Section */}
      <div className="p-4">
        <UserMessageForm
          isDisabled={isLoading}
          onMessageSend={handleMessageSend}
        />
      </div>
    </>
  );
};
